import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Modal,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import mammoth from "mammoth";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

// ✅ OTP services
import { apigetOtp, apipostOtp, apipostOtpFormData } from "../../service/otpApi";
import { constant } from "../../constant";
import { formatRelativeTime } from "../../utils/formatTime";
import { useBorrowerLoan } from "../../layouts/borrower/BorrowerLoanContext";

const BorrowerLoanDetail = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const { loan } = useBorrowerLoan();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [viewUrl, setViewUrl] = useState("");
  const [viewCanPreview, setViewCanPreview] = useState(true);
  const [viewFileName, setViewFileName] = useState("");
  const [viewDocHtml, setViewDocHtml] = useState(""); // For mammoth .docx rendering
  const [dragOverDocId, setDragOverDocId] = useState(null);

  // Snackbar notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const showNotification = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const bounceIfUnauthed = (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      // clear any stale token (optional)
      sessionStorage.removeItem("borrowerToken");
      sessionStorage.removeItem("borrowerUser");
      navigate("/borrower/login", { replace: true });
      return true;
    }
    return false;
  };

  const updateProgress = (list) => {
    if (!list.length) {
      setProgress(0);
      return;
    }
    const completed = list.filter(
      (d) => d.status === "Received" || d.status === "Uploaded to Encompass"
    ).length;
    setProgress(Math.round((completed / list.length) * 100));
  };

  // ✅ Fetch doc requests for this loan (OTP-auth)
  const fetchDocs = async () => {
    setLoading(true);
    try {
      // Update this route to your OTP-protected route
      // (recommended: server should verify borrower owns this loanId)
      const res = await apigetOtp(`otp/borrower/loan/${loanId}/request-documents`);
      const results = res?.data?.result || [];
      setDocs(results);
      updateProgress(results);
    } catch (err) {
      if (bounceIfUnauthed(err)) return;
      console.error("Error fetching loan docs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId]);

  // Core upload processor — accepts files from button picker or drag-and-drop
  const processUploadFiles = async (docId, docName, files) => {
    if (files.length === 0) return;

    setUploadingId(docId);
    try {
      let anyDeferred = false;
      await files.reduce((chain, file) => {
        return chain.then(async () => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("loanId", loanId);
          formData.append("documentName", docName);
          const uploadRes = await apipostOtpFormData(`otp/borrower/upload-doc/${loanId}`, formData);
          if (uploadRes?.data?.deferred) {
            anyDeferred = true;
          }
        });
      }, Promise.resolve());

      await apipostOtp(`otp/borrower/loan/${loanId}/request-documents`, {
        loanId,
        documents: [{ name: docName, status: "Received" }],
      });

      if (anyDeferred) {
        showNotification(
          "Your document was uploaded successfully! It will be organized by your loan officer shortly.",
          "success"
        );
      } else {
        showNotification("Document uploaded successfully!", "success");
      }

      fetchDocs();
    } catch (err) {
      if (bounceIfUnauthed(err)) return;
      console.error("Error uploading document:", err);
      showNotification(
        err?.response?.data?.message || "Failed to upload document. Please try again.",
        "error"
      );
    } finally {
      setUploadingId(null);
    }
  };

  // Upload via file picker button
  const handleUpload = (docId, docName) => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "application/pdf,image/*,.doc,.docx,.txt";
    fileInput.multiple = true;
    fileInput.click();

    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files || []);
      processUploadFiles(docId, docName, files);
    };
  };

  // --- Drag-and-drop upload ---
  const UPLOADABLE_STATUSES = ["Requested", "Added", "Rejected", "Received"];

  const handleDragOver = (e, docId, status) => {
    if (!UPLOADABLE_STATUSES.includes(status)) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocId(docId);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDocId(null);
    }
  };

  const handleDrop = (e, docId, docName, status) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDocId(null);

    if (!UPLOADABLE_STATUSES.includes(status)) return;

    const droppedFiles = Array.from(e.dataTransfer.files || []);
    const validFiles = droppedFiles.filter(
      (f) =>
        f.type.startsWith("application/pdf") ||
        f.type.startsWith("image/") ||
        f.type === "application/msword" ||
        f.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        f.type === "text/plain" ||
        f.name.match(/\.(pdf|png|jpe?g|gif|bmp|webp|doc|docx|txt)$/i)
    );

    if (validFiles.length === 0) {
      showNotification("No supported files found. Please upload PDF, image, Word, or text files.", "warning");
      return;
    }

    if (validFiles.length < droppedFiles.length) {
      showNotification(
        `${droppedFiles.length - validFiles.length} unsupported file(s) were skipped.`,
        "info"
      );
    }

    processUploadFiles(docId, docName, validFiles);
  };

  // Prevent browser from opening dropped files outside cards
  useEffect(() => {
    const preventBrowserDrop = (e) => e.preventDefault();
    window.addEventListener("dragover", preventBrowserDrop);
    window.addEventListener("drop", preventBrowserDrop);
    return () => {
      window.removeEventListener("dragover", preventBrowserDrop);
      window.removeEventListener("drop", preventBrowserDrop);
    };
  }, []);

  // Types that browsers can render inline in an iframe
  const canPreviewInline = (blobType) => {
    if (!blobType) return false;
    return (
      blobType.startsWith("application/pdf") ||
      blobType.startsWith("image/") ||
      blobType.startsWith("text/")
    );
  };

  // Detect Word document types (.docx) for mammoth conversion
  const isWordDocument = (blobType, fileName) => {
    if (
      blobType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      blobType === "application/msword"
    ) {
      return true;
    }
    // Fallback: check filename extension
    const ext = (fileName || "").split(".").pop().toLowerCase();
    return ext === "docx" || ext === "doc";
  };

  // Fetch document as blob (iframe can't send Authorization headers)
  const handleView = async (docId, attachmentIndex = 0) => {
    try {
      const token = sessionStorage.getItem("borrowerToken") || "";
      const response = await axios.get(
        `${constant.baseUrl}otp/borrower/files/${loanId}/${docId}?attachmentIndex=${attachmentIndex}`,
        {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      let blob = response.data;
      if (blob.type === "application/octet-stream") {
        const ct = response.headers["content-type"] || "application/pdf";
        blob = new Blob([blob], { type: ct });
      }

      // Find the doc to get the filename
      const doc = docs.find((d) => d._id === docId);
      const fileName =
        doc?.attachments?.[attachmentIndex]?.fileName ||
        doc?.fileOriginalName ||
        doc?.documentName ||
        "document";

      // Check if this is a Word document we can convert with mammoth
      if (isWordDocument(blob.type, fileName)) {
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setViewDocHtml(result.value);
          setViewUrl(""); // No blob URL needed for HTML rendering
          setViewCanPreview(true);
          setViewFileName(fileName);
          setOpenModal(true);
          return;
        } catch (convErr) {
          console.error("Mammoth conversion failed:", convErr);
          // Fall through to normal blob handling
        }
      }

      const blobUrl = URL.createObjectURL(blob);
      setViewUrl(blobUrl);
      setViewDocHtml(""); // Clear any previous HTML
      setViewCanPreview(canPreviewInline(blob.type));
      setViewFileName(fileName);
      setOpenModal(true);
    } catch (err) {
      if (bounceIfUnauthed(err)) return;
      console.error("Error loading document preview:", err);
      showNotification("Failed to load document preview.", "error");
    }
  };

  const handleCloseModal = () => {
    if (viewUrl) URL.revokeObjectURL(viewUrl);
    setOpenModal(false);
    setViewUrl("");
    setViewDocHtml("");
    setViewCanPreview(true);
    setViewFileName("");
  };

  // Map internal status to borrower-friendly label
  const getBorrowerStatusLabel = (status) => {
    if (status === "Uploaded to Encompass") return "Accepted";
    if (status === "Rejected") return "Re-upload Required";
    return status;
  };

  const getChipColor = (status) => {
    switch (status) {
      case "Added":
        return "default";
      case "Requested":
        return "info";
      case "Received":
        return "success";
      case "Uploaded to Encompass":
        return "secondary";
      case "Rejected":
        return "error";
      default:
        return "warning";
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Document Center
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Upload Progress: {progress}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: "#e0e0e0",
            "& .MuiLinearProgress-bar": {
              bgcolor: progress === 100 ? "success.main" : "primary.main",
            },
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : docs.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
          <DescriptionIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No documents have been requested yet.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your loan officer will request documents when they&apos;re needed.
            You&apos;ll be notified by email.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {docs.map((doc) => {
            const hasFiles = (doc.attachments?.length > 0) || doc.encompassAttachmentId;
            const fileCount = doc.attachments?.length || (doc.encompassAttachmentId ? 1 : 0);

            const isDragOver = dragOverDocId === doc._id;

            return (
              <Card
                key={doc._id}
                onDragOver={(e) => handleDragOver(e, doc._id, doc.status)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, doc._id, doc.documentName, doc.status)}
                sx={{
                  borderRadius: 2,
                  boxShadow: isDragOver ? 8 : 3,
                  "&:hover": { boxShadow: 8 },
                  border: isDragOver ? "2px dashed" : "2px solid transparent",
                  borderColor: isDragOver ? "primary.main" : "transparent",
                  bgcolor: isDragOver ? "rgba(25, 118, 210, 0.04)" : "background.paper",
                  transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s",
                  position: "relative",
                }}
              >
                <CardContent>
                  {/* Drop zone overlay */}
                  {isDragOver && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(25, 118, 210, 0.08)",
                        borderRadius: 2,
                        zIndex: 10,
                        pointerEvents: "none",
                      }}
                    >
                      <Typography variant="h6" color="primary" fontWeight={600}>
                        Drop files here to upload
                      </Typography>
                    </Box>
                  )}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {doc.documentName}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Chip
                          label={getBorrowerStatusLabel(doc.status)}
                          color={getChipColor(doc.status)}
                          size="small"
                        />
                        {fileCount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {fileCount} file{fileCount !== 1 ? "s" : ""} uploaded
                          </Typography>
                        )}
                        {doc.modifiedOn && (
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(doc.modifiedOn)}
                          </Typography>
                        )}
                      </Stack>
                      {doc.attachments && doc.attachments.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ mt: 0.5, color: "text.secondary", display: "block" }}
                        >
                          {doc.attachments.map((att) => att.fileName).join(" \u2022 ")}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {/* Upload button - show for Requested/Added/Rejected, or "Upload More" for Received */}
                      {(doc.status === "Requested" || doc.status === "Added" || doc.status === "Rejected") && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={uploadingId === doc._id ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                          disabled={uploadingId === doc._id}
                          onClick={() => handleUpload(doc._id, doc.documentName)}
                        >
                          {uploadingId === doc._id ? "Uploading..." : "Upload"}
                        </Button>
                      )}

                      {doc.status === "Received" && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={uploadingId === doc._id ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                          disabled={uploadingId === doc._id}
                          onClick={() => handleUpload(doc._id, doc.documentName)}
                        >
                          {uploadingId === doc._id ? "Uploading..." : "Upload More"}
                        </Button>
                      )}

                      {/* View buttons - show per attachment if multiple */}
                      {hasFiles && doc.attachments && doc.attachments.length > 1 ? (
                        doc.attachments.map((att, idx) => (
                          <Button
                            key={idx}
                            variant="outlined"
                            size="small"
                            color="primary"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleView(doc._id, idx)}
                          >
                            View {idx + 1}
                          </Button>
                        ))
                      ) : hasFiles ? (
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleView(doc._id, 0)}
                        >
                          View
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>

                  {/* Show rejection reason if document was rejected */}
                  {doc.rejectionReason && doc.status === "Rejected" && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        bgcolor: "#fff3f3",
                        borderRadius: 1,
                        borderLeft: "3px solid #d32f2f",
                      }}
                    >
                      <Typography variant="caption" color="error" fontWeight={600}>
                        Re-upload Required:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {doc.rejectionReason}
                      </Typography>
                    </Box>
                  )}

                  {/* Show loan officer comment if present */}
                  {doc.comment && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                        borderLeft: "3px solid #1976d2",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Note from your Loan Officer:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {doc.comment}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            height: "90%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #ddd",
              bgcolor: "#f9f9f9",
            }}
          >
            <Typography variant="h6">Document Preview</Typography>
            <Button color="error" onClick={handleCloseModal}>
              Close
            </Button>
          </Box>

          {/* Mammoth-rendered Word document */}
          {viewDocHtml ? (
            <Box
              sx={{
                p: 3,
                height: "calc(100% - 64px)",
                overflow: "auto",
                "& img": { maxWidth: "100%" },
                "& table": {
                  borderCollapse: "collapse",
                  width: "100%",
                  "& td, & th": {
                    border: "1px solid #ddd",
                    padding: "8px",
                  },
                },
              }}
              dangerouslySetInnerHTML={{ __html: viewDocHtml }}
            />
          ) : viewUrl && viewCanPreview ? (
            <iframe
              src={viewUrl}
              title="Document Preview"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : viewUrl && !viewCanPreview ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "80%",
                gap: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                This file type cannot be previewed in the browser.
              </Typography>
              <Button
                variant="contained"
                href={viewUrl}
                download={viewFileName}
              >
                Download File
              </Button>
            </Box>
          ) : (
            <Typography sx={{ p: 4, textAlign: "center" }} color="text.secondary">
              No document available for preview.
            </Typography>
          )}
        </Box>
      </Modal>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", fontSize: "0.95rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BorrowerLoanDetail;
